if (!customElements.get('product-form')) {
  // PRODUCT_HANDLE / PRODUCT are defined by the main-product (and subscription-product)
  // section inline scripts. product-form.js is ALSO loaded on collection grids and featured
  // collection/product sections where those globals don't exist — and the defining PDP script
  // can itself fail to run. Read the handle defensively so a missing global degrades to '' and
  // never throws a ReferenceError that would break the add-to-cart promise chain.
  const getProductHandle = () =>
    (typeof PRODUCT_HANDLE !== 'undefined' && PRODUCT_HANDLE) || (window.PRODUCT_HANDLE || '');
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      announceToScreenReader(message) {
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.className = 'sr-only';
        announcement.textContent = message;
        this.form.appendChild(announcement);
        
        // Remove the announcement after it's been read
        setTimeout(() => {
          announcement.remove();
        }, 8000);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;


        //check cart rules here before add to cart action is fired
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            // console.log(cart);
            // Check for cart with products trying to purchase Cookie Club
            if(cart.items.length > 0 && getProductHandle().includes('cookie-club')){
              const errorMessage = 'Levain Cookie Club subscription must be purchased separately and only one subscription may be purchased at a time. Please checkout or empty your cart to continue shopping.';
              this.handleErrorMessage(errorMessage);
              this.announceToScreenReader(errorMessage);
              throw new Error('Tried to add Cookie Club with product in cart.');
            }
            // check for cart with Subscription already in it
            cart.items.forEach(item => {
              if(item.product_title.includes('Cookie Club')){
                const errorMessage = 'Levain Cookie Club subscription must be purchased separately and only one subscription may be purchased at a time. Please checkout or empty your cart to continue shopping.';
                this.handleErrorMessage(errorMessage);
                this.announceToScreenReader(errorMessage);
                throw new Error('Tried to add product with Cookie Club in cart.');
              }
            });
            if(cart.items.length > 0 && getProductHandle().includes('event-sale')){
              const errorMessage = 'Book event pre-order must be purchased separately. Please checkout or empty your cart to continue shopping.';
              this.handleErrorMessage(errorMessage);
              this.announceToScreenReader(errorMessage);
              throw new Error('Tried to add Event Sale with product in cart.');
            }
            // check for cart with Subscription already in it
            cart.items.forEach(item => {
              if(item.product_title.includes('Book Event')){
                const errorMessage = 'Book event pre-order must be purchased separately. Please checkout or empty your cart to continue shopping.';
                this.handleErrorMessage(errorMessage);
                this.announceToScreenReader(errorMessage);
                throw new Error('Tried to add product with Event Sale in cart.');
              }
            });
            // check for empty cart trying to add merch           
            const merchHandles = ['levain-holiday-ornament', 'levain-holiday-ornament-1', 'nyc-taxi-holiday-ornament', 'taxi-cab-tote-bag', 'bicycle-tote', 'levain-tote-bag', 'levain-onesie', 'levain-tea-towel', 'levain-baseball-cap', 'levain-pool-float', 'dad-mug']
            if(cart.items.length === 0 && merchHandles.includes(getProductHandle())){
              const errorMessage = 'Merch can only ship with cookies. Please add cookies to your cart first.';
              this.handleErrorMessage(errorMessage);
              this.announceToScreenReader(errorMessage);
              throw new Error('Tried to add Merch to cart without Cookies.');
            }

            // check for 30th anniversary book in cart when trying to add cookies
            const hasBookInCart = cart.items.some(item => item.handle === '30th-anniversary-book');
            const hasEventInCart = cart.items.some(item => item.handle === 'event-sale');
            
            if(hasEventInCart && !merchHandles.includes(getProductHandle())){
              const errorMessage = 'The 30th Anniversary Book Signing event must be pre-ordered and purchased without cookies. Please checkout or empty your cart to continue shopping.';
              this.handleErrorMessage(errorMessage);
              this.announceToScreenReader(errorMessage);
              throw new Error('Tried to add cookies to cart with 30th Anniversary Book.');
            }

            // check for existing items in cart when trying to add the 30th anniversary book
            if(getProductHandle() === 'event-sale' && cart.items.length > 0){
              const errorMessage = 'The 30th Anniversary Book signing event must be pre-ordered and purchased without cookies. Please checkout or empty your cart to continue shopping.';
              this.handleErrorMessage(errorMessage);
              this.announceToScreenReader(errorMessage);
              throw new Error('Tried to add 30th Anniversary Book Signing Event to cart with existing items.');
            }

            // check for cart with products trying to purchase gift card
            if(cart.items.length > 0 && getProductHandle() === 'rise-ai-giftcard'){
              const errorMessage = 'E-Gift Cards must be purchased separately. Please checkout or empty your cart to continue shopping.';
              this.handleErrorMessage(errorMessage);
              this.announceToScreenReader(errorMessage);
              throw new Error('Tried to add gift card with product in cart.');
            }
            // check for cart with gift card already in it
            cart.items.forEach(item => {
              if(item.handle === 'rise-ai-giftcard'){
                const errorMessage = 'E-Gift Cards must be purchased separately. Please checkout or empty your cart to continue shopping.';
                this.handleErrorMessage(errorMessage);
                this.announceToScreenReader(errorMessage);
                throw new Error('Tried to add product with gift card in cart.');
              }
            });
            
          })
          .then(async () => {
            // Check for tin GWP upgrade before adding to cart
            const originalVariantId = formData.get('id');
            if (originalVariantId &&
                typeof window.TIN_GWP_ENABLED !== 'undefined' &&
                window.TIN_GWP_ENABLED &&
                typeof PRODUCT !== 'undefined') {
              const originalVariant = PRODUCT.variants.find(v => v.id == originalVariantId);
              if (originalVariant && originalVariant.sku) {
                const tinVariantId = getTinUpgradeVariantId(originalVariant.sku);
                if (tinVariantId) {
                  // Replace the variant ID with the tin variant ID
                  formData.set('id', tinVariantId);
                  console.log('Tin GWP: Upgrading variant from', originalVariantId, 'to', tinVariantId);
                  // Apply the Tin GWP discount code before the add request so the
                  // rendered cart sections show the tin as discounted.
                  if (typeof window.applyTinGWPDiscountCode === 'function') {
                    await window.applyTinGWPDiscountCode();
                  }
                }
              }
            }

            // Direct add of a qualifying tin (e.g. a 12 PK tin during a 12 PK promo): apply
            // the promo code too, same as when a cookie box is swapped for a tin above.
            if (typeof window.isQualifyingTinGWPVariant === 'function'
                && window.isQualifyingTinGWPVariant(formData.get('id'))
                && typeof window.applyTinGWPDiscountCode === 'function') {
              await window.applyTinGWPDiscountCode();
            }

            // Bundle component check — if the selected variant has component IDs defined,
            // add only the parent bundle item to the cart. The Cart Transform Function will
            // expand the parent into its component line items at checkout. The cart drawer
            // shows component names via the custom.bundle_component_variant_ids metafield
            // (rendered in cart-line-item.liquid).
            const selectedVariantId = parseInt(formData.get('id'), 10);
            const bundleData = window.BUNDLE_COMPONENT_VARIANTS?.[selectedVariantId];

            let cartFetch;
            if (bundleData && bundleData.componentIds && bundleData.componentIds.length > 0) {
              const bundleConfig = fetchConfig('javascript');
              bundleConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
              bundleConfig.body = JSON.stringify({
                items: [
                  {
                    id: selectedVariantId,
                    quantity: 1,
                    properties: {
                      _is_bundle_parent: 'true',
                      _bundle_title: bundleData.bundleTitle || '',
                    },
                  },
                  {
                    id: 59146737746001, // UrbanStems Postcard
                    quantity: 1,
                  },
                ],
                sections: this.cart ? this.cart.getSectionsToRender().map(s => s.id) : [],
                sections_url: window.location.pathname,
              });
              cartFetch = fetch(`${routes.cart_add_url}`, bundleConfig);
            } else {
              cartFetch = fetch(`${routes.cart_add_url}`, config);
            }

            return cartFetch
            .then((response) => response.json())
            .then(async (response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);
                this.announceToScreenReader(response.description);
  
                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }
  
              if (!this.error) {
                let cartStateForUI = response;

                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: cartStateForUI,
                });
                this.announceToScreenReader('Product successfully added to cart');
              
                this.error = false;
                const quickAddModal = this.closest('quick-add-modal');

                // Auto-add the GWP if the cart now qualifies. autoAddGWP fetches the live
                // cart itself and checks each item's variant_title/product_type against the
                // configured criteria, so no per-product eligibility check is needed here.
                if (window.GWP_ACTIVE && window.cartGWPUtils && typeof window.cartGWPUtils.autoAddGWP === 'function') {
                  const sections = this.cart ? this.cart.getSectionsToRender().map(s => s.id) : [];
                  cartStateForUI = await window.cartGWPUtils.autoAddGWP(cartStateForUI, {
                    sections,
                    sectionsUrl: window.location.pathname
                  });
                }

                if (quickAddModal) {
                  document.body.addEventListener(
                    'modalClosed',
                    () => {
                      setTimeout(() => {
                        this.cart.renderContents(cartStateForUI);
                      });
                    },
                    { once: true }
                  );
                  quickAddModal.hide(true);
                } else {
                  this.cart.renderContents(cartStateForUI);
                }
              }
            })
            .catch((e) => {
              console.error(e);
              this.announceToScreenReader('Error adding product to cart');
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');

              // Show upsell popup if applicable
              this.showUpsellPopup();
            });
          })
          .catch((e) => {
            console.error(e);
            // Reset Rise AI widget after error if we're on the gift card product
            if (getProductHandle() === 'rise-ai-giftcard') {
              this.resetRiseAIWidget();
            }
          }).finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            //upsell
          });   
      }

      showUpsellPopup(){
        // PRODUCT is only defined on the main-product / subscription-product PDP scripts.
        // On collection grids and featured sections that share product-form.js it is absent,
        // so bail out (no upsell applies there) instead of throwing on PRODUCT.variants.
        if (typeof PRODUCT === 'undefined' || !PRODUCT) return false;
        const selectedVariantId = this.form.querySelector('input[name="id"]').value;
        const sku = PRODUCT.variants.find(variant => variant.id == selectedVariantId)?.sku;
        const upsellPrefixes = ['GB04', 'GB08', 'GB12']
        //upsell only for cookie boxes and new parents bundle
        if(!sku) return false;
        if(upsellPrefixes.includes(sku.split('-')[0]) === false && getProductHandle() !== 'new-parents-bundle') {
          if (window.COLLECTION_CROSS_SELL && window.COLLECTION_CROSS_SELL.variantId) {
            return this.showCollectionCrossSell();
          }
          return false;
        }

        const sizeSelector = document.querySelector('.upsell-size-selector');
        sizeSelector.classList.add('hidden');
        
        // For New Parents Bundle, create the necessary hidden inputs that cart-drawer.js expects
        if (getProductHandle() === 'new-parents-bundle') {
          this.createHiddenVariantInputs(selectedVariantId, sku);
        }
        
        // Find the selected variant input in a cleaner way
        const selectedVariantInput = this.getSelectedVariantInput();
        
        let upsellSelector;
        let upsellProduct;
        let upsellFallbackProduct;
        let upsellProductB;
        let upsellProductBFallback;
        let upsellType;
        let selectedVariantSize;
        let selectedVariantSku;
        let upsellSecondaryVariantId;
        
        if (getProductHandle() === 'new-parents-bundle') {
          upsellSelector = 'NEW-PARENTS';
          upsellProduct = UPSELL_PRODUCT_NEW_PARENTS;
          upsellType = UPSELL_TYPE_NEW_PARENTS;
          // For new parents bundle, use the first variant if no variant input is found
          if (!selectedVariantInput) {
            selectedVariantSize = 'NEW-PARENTS';
            selectedVariantSku = sku;
          } else {
            selectedVariantSize = selectedVariantInput.value;
            selectedVariantSku = selectedVariantInput.dataset.variantSku;
          }
        } else {
          if (!selectedVariantInput) {
            // Single-size products (e.g. single-cookie flavors) have no Pack Size selector.
            // Derive the pack size from the SKU prefix so the fallback upsell can still fire.
            const skuPrefix = sku?.split('-')[0];
            const sizeFromSku = skuPrefix === 'GB04' ? '4 PK' : skuPrefix === 'GB08' ? '8 PK' : skuPrefix === 'GB12' ? '12 PK' : null;
            if (!sizeFromSku) return false;
            selectedVariantSize = sizeFromSku;
            selectedVariantSku = sku;
          } else {
            selectedVariantSize = selectedVariantInput.value;
            selectedVariantSku = selectedVariantInput.dataset.variantSku;
          }
          // Per-pack-size kill switch (Theme settings > N Pack Upsell > Show N Pack Upsell)
          const packSizeKey = selectedVariantSize.split(' ')[0];
          if (window.UPSELL_ENABLED && window.UPSELL_ENABLED[packSizeKey] === false) return false;
          upsellSelector =selectedVariantSize.split(" ")[1]+ "-" +selectedVariantSize.split(" ")[0];
          if( selectedVariantSize == '4 PK'){
            upsellProduct = UPSELL_PRODUCT_4PK;
            upsellType = UPSELL_TYPE_4PK;
          } else if(selectedVariantSize == '8 PK'){
            upsellProduct = UPSELL_PRODUCT_8PK;
            upsellType = UPSELL_TYPE_8PK;
            upsellFallbackProduct = typeof UPSELL_PRODUCT_8PK_FALLBACK !== 'undefined' ? UPSELL_PRODUCT_8PK_FALLBACK : null;
            upsellProductB = typeof UPSELL_PRODUCT_8PK_B !== 'undefined' ? UPSELL_PRODUCT_8PK_B : null;
            upsellProductBFallback = typeof UPSELL_PRODUCT_8PK_B_FALLBACK !== 'undefined' ? UPSELL_PRODUCT_8PK_B_FALLBACK : null;
          } else  if(selectedVariantSize == '12 PK'){
            upsellProduct = UPSELL_PRODUCT_12PK;
            upsellType = UPSELL_TYPE_12PK;
            upsellFallbackProduct = typeof UPSELL_PRODUCT_12PK_FALLBACK !== 'undefined' ? UPSELL_PRODUCT_12PK_FALLBACK : null;
            upsellProductB = typeof UPSELL_PRODUCT_12PK_B !== 'undefined' ? UPSELL_PRODUCT_12PK_B : null;
            upsellProductBFallback = typeof UPSELL_PRODUCT_12PK_B_FALLBACK !== 'undefined' ? UPSELL_PRODUCT_12PK_B_FALLBACK : null;
          }
        }

        // console.log(upsellSelector);
        const upsellTitleEl = document.querySelector('.upsell-title.' + upsellSelector);
        upsellTitleEl.classList.remove('hidden');
        upsellTitleEl.removeAttribute('aria-hidden');

        let upsellVariantId;

        if( upsellType === 'upsell_clothing'){
          sizeSelector.classList.remove('hidden');
          //Upsell, map original product to upsell product based on sku
          const upsellSkuSuffix = selectedVariantSku.split('-')[1];
          const selectedUpsellSize = document.querySelector('input[name="upsellSize"]:checked').value;
          const upsellPrefix = selectedVariantSku.split('-')[0].replace('GB',''); + selectedUpsellSize.toLowerCase();

          
          upsellProduct.variants.forEach(variant => {
            if(variant.sku.split('-')[0].includes(upsellPrefix) && variant.sku.split('-')[1] === upsellSkuSuffix ){
              upsellVariantId = variant.id;
            }
          })


        } 
        else if( upsellType === 'upsell'){
          const upsellSkuSuffix = selectedVariantSku.split('-')[1];
          const upsellPrefix = selectedVariantSku.split('-')[0].replace('GB','');

          // Tin 1 (Option A): try primary, fall back to Tin 1 fallback
          upsellProduct.variants.forEach(variant => {
            if(variant.sku && variant.sku.split('-')[0].includes(upsellPrefix) && variant.sku.split('-')[1] === upsellSkuSuffix ){
              upsellVariantId = variant.id;
            }
          })
          if(!upsellVariantId && upsellFallbackProduct && Array.isArray(upsellFallbackProduct.variants)) {
            upsellFallbackProduct.variants.forEach(variant => {
              if(variant.sku && variant.sku.split('-')[0].includes(upsellPrefix) && variant.sku.split('-')[1] === upsellSkuSuffix ){
                upsellVariantId = variant.id;
              }
            })
          }

          // Tin 2 (Option B): try primary, fall back to Tin 2 fallback
          if(upsellProductB && Array.isArray(upsellProductB.variants)) {
            upsellProductB.variants.forEach(variant => {
              if(variant.sku && variant.sku.split('-')[0].includes(upsellPrefix) && variant.sku.split('-')[1] === upsellSkuSuffix ){
                upsellSecondaryVariantId = variant.id;
              }
            })
          }
          if(!upsellSecondaryVariantId && upsellProductBFallback && Array.isArray(upsellProductBFallback.variants)) {
            upsellProductBFallback.variants.forEach(variant => {
              if(variant.sku && variant.sku.split('-')[0].includes(upsellPrefix) && variant.sku.split('-')[1] === upsellSkuSuffix ){
                upsellSecondaryVariantId = variant.id;
              }
            })
          }
        }
        else if( upsellType === 'cross-sell'){
          //cross sell, should be single variant
          upsellProduct.variants.forEach(variant => {
            upsellVariantId = variant.id;
          })
        }

        if(!upsellVariantId){
          return false;
        }

        // Show the right content layout for this pack size
        if (selectedVariantSize === '8 PK' || selectedVariantSize === '12 PK') {
          const packNum = selectedVariantSize === '8 PK' ? '8' : '12';
          if (upsellSecondaryVariantId) {
            // Two options available — show choice cards
            document.querySelector(`.upsell-two-options--${packNum}`).classList.remove('hidden');
            document.querySelector('input[name="Upsell Variant Id Secondary"]').value = upsellSecondaryVariantId;
          } else {
            // Single option — show flat layout with price in button
            document.querySelector(`.upsell-single-option--${packNum}`).classList.remove('hidden');
            document.querySelector('.upsell-variant-price.' + upsellSelector).classList.remove('hidden');
          }
        } else {
          // 4 PK and New Parents — existing flat layout
          document.querySelector('.upsell-img.'+ upsellSelector)?.classList.remove('hidden');
          document.querySelector('.upsell-copy.'+ upsellSelector).classList.remove('hidden');
          document.querySelector('.upsell-variant-price.'+ upsellSelector).classList.remove('hidden');
        }

        document.querySelector('input[name="Upsell Variant Id"]').value = upsellVariantId;
        document.querySelector('input[name="Upsell Type"]').value = upsellType;

        const upsellSection = document.getElementById('CartUpsellSection');
        upsellSection.setAttribute('aria-hidden', 'false');
        // Defer .active to fire in the same setTimeout as cart-drawer's open() animation
        // so both slide in together on the same frame instead of sequentially.
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer._pendingPanel = upsellSection;
        } else {
          upsellSection.classList.add('active');
        }

        // B1: Trap focus inside the upsell panel with a focus-safety delay
        setTimeout(() => {
          const firstFocusable = upsellSection.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
          if (typeof trapFocus === 'function') trapFocus(upsellSection, firstFocusable || upsellSection);
        }, 100);

        // B1: ESC key dismissal
        const escHandler = (e) => {
          if (e.key === 'Escape') {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.closeUpsell === 'function') cartDrawer.closeUpsell();
            document.removeEventListener('keydown', escHandler);
          }
        };
        document.addEventListener('keydown', escHandler);

        // E1: Announce panel open
        const liveRegion = upsellSection.querySelector('#CartUpsellAnnouncement');
        if (liveRegion) liveRegion.textContent = 'Upgrade panel opened.';

        return true;
      }

      showCollectionCrossSell() {
        const config = window.COLLECTION_CROSS_SELL;
        if (!config || !config.variantId) return false;

        const upsellSection = document.getElementById('CartUpsellSection');
        if (!upsellSection) return false;

        // Hide all pack/new-parents/byo-tin content so only cross-sell shows.
        // Mirrors the selector set used in configureByoTinUpsellPanel in build-your-own.js.
        upsellSection.querySelectorAll('.PK-4, .PK-8, .PK-12, .NEW-PARENTS').forEach(el => {
          el.classList.add('hidden');
          el.setAttribute('aria-hidden', 'true');
        });
        upsellSection.querySelectorAll('.byo-tin-upsell').forEach(el => el.classList.add('hidden'));
        upsellSection.querySelectorAll('.upsell-single-option, .upsell-two-options').forEach(el => el.classList.add('hidden'));
        upsellSection.querySelectorAll('.upsell-size-selector').forEach(el => el.classList.add('hidden'));
        upsellSection.querySelectorAll('.upsell-variant-price:not(.COLLECTION-CROSS-SELL)').forEach(el => el.classList.add('hidden'));

        // Populate heading.
        const heading = upsellSection.querySelector('.upsell-title.COLLECTION-CROSS-SELL');
        if (heading) {
          heading.textContent = config.title || 'Add to your order!';
          heading.classList.remove('hidden');
          heading.removeAttribute('aria-hidden');
        }

        // Populate image and copy block.
        const crossSellDiv = upsellSection.querySelector('.collection-cross-sell');
        if (crossSellDiv) {
          const img = crossSellDiv.querySelector('.collection-cross-sell__img');
          if (img) {
            if (config.image) {
              img.src = config.image;
              img.style.display = '';
            } else {
              img.style.display = 'none';
            }
          }
          const copy = crossSellDiv.querySelector('.collection-cross-sell__copy');
          if (copy) copy.textContent = config.copy || '';
          crossSellDiv.classList.remove('hidden');
        }

        // Populate price span.
        const priceSpan = upsellSection.querySelector('.upsell-variant-price.COLLECTION-CROSS-SELL');
        if (priceSpan) {
          if (config.price) {
            priceSpan.textContent = ` +${config.price}`;
            priceSpan.classList.remove('hidden');
          } else {
            priceSpan.classList.add('hidden');
          }
        }

        // Wire hidden inputs for cart-drawer.js addUpsellToCart() cross-sell branch.
        const typeInput = upsellSection.querySelector('input[name="Upsell Type"]');
        const variantInput = upsellSection.querySelector('input[name="Upsell Variant Id"]');
        const secondaryInput = upsellSection.querySelector('input[name="Upsell Variant Id Secondary"]');
        if (typeInput) typeInput.value = 'cross-sell';
        if (variantInput) variantInput.value = config.variantId;
        if (secondaryInput) secondaryInput.value = '';

        // Reveal panel — same mechanism as showUpsellPopup tail.
        upsellSection.setAttribute('aria-hidden', 'false');
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer._pendingPanel = upsellSection;
        } else {
          upsellSection.classList.add('active');
        }

        // Trap focus inside the panel after slide-in animation starts.
        setTimeout(() => {
          const firstFocusable = upsellSection.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
          if (typeof trapFocus === 'function') trapFocus(upsellSection, firstFocusable || upsellSection);
        }, 100);

        // ESC key dismissal.
        const escHandler = (e) => {
          if (e.key === 'Escape') {
            const cd = document.querySelector('cart-drawer');
            if (cd && typeof cd.closeUpsell === 'function') cd.closeUpsell();
            document.removeEventListener('keydown', escHandler);
          }
        };
        document.addEventListener('keydown', escHandler);

        // Announce panel open to screen readers.
        const liveRegion = upsellSection.querySelector('#CartUpsellAnnouncement');
        if (liveRegion) liveRegion.textContent = config.title || 'Add to your order!';

        return true;
      }

      createHiddenVariantInputs(variantId, sku) {
        // Remove any existing hidden inputs to avoid duplicates
        const existingInputs = document.querySelectorAll('input[name="Pack Size"][data-new-parents-bundle]');
        existingInputs.forEach(input => input.remove());
        
        // Create a hidden input that cart-drawer.js can find
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'radio';
        hiddenInput.name = 'Pack Size';
        hiddenInput.value = 'NEW-PARENTS';
        hiddenInput.dataset.variantId = variantId;
        hiddenInput.dataset.variantSku = sku;
        hiddenInput.dataset.newParentsBundle = 'true';
        hiddenInput.checked = true;
        hiddenInput.style.display = 'none';
        
        // Add it to the document body so it can be found by cart-drawer.js
        document.body.appendChild(hiddenInput);
      }

      getSelectedVariantInput() {
        // First try to find the input within this form
        let input = this.form.querySelector('input[name="Pack Size"]:checked') || 
                    this.form.querySelector('input[name="Size"]:checked');
        
        // If not found in form, try document-wide
        if (!input) {
          input = document.querySelector('input[name="Pack Size"]:checked') || 
                  document.querySelector('input[name="Size"]:checked');
        }
        
        return input;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      resetRiseAIWidget() {
        // Reload the Rise AI iframe to reset its state
        try {
          // Try multiple iframe selectors
          let riseIframe = document.querySelector('iframe[src*="rise"]');
          
          if (!riseIframe) {
            // Try finding by other common patterns
            riseIframe = document.querySelector('iframe[id*="rise"]');
          }
          
          if (!riseIframe) {
            riseIframe = document.querySelector('iframe[class*="rise"]');
          }
          
          if (!riseIframe) {
            // Check all iframes
            const allIframes = document.querySelectorAll('iframe');
            console.log('All iframes found:', allIframes);
            allIframes.forEach(iframe => {
              console.log('Iframe src:', iframe.src, 'id:', iframe.id, 'class:', iframe.className);
            });
          }
          
          if (riseIframe) {
            console.log('Found Rise AI iframe, resetting...');
            const src = riseIframe.src;
            riseIframe.src = 'about:blank';
            setTimeout(() => {
              riseIframe.src = src;
              console.log('Rise AI iframe reset complete');
            }, 100);
          } else {
            console.log('No Rise AI iframe found');
          }
        } catch (error) {
          console.log('Could not reset Rise AI widget:', error);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
